/**
 * Mirror of shaders/fieldColour.glsl: the colour of the polders as the painter laid it, and his
 * parcel-scale tone, which the painted material gives a field before its ditches and texture.
 *
 * Measured in the painter view against the painting, on the field pixels its labels give, through
 * the residual warp, the painting's water structures and every pixel not nearest a field class left
 * out (terrain-colour, 2026-09-27): the render's field colour varied from parcel to parcel (strip
 * between two ditches, 150 m along it) by a fifth to a third of the painting's (std L*, a*, b* 0.52,
 * 0.43, 0.45 against 1.34, 1.27, 1.38 in painter-frame rows 100-250; 0.61, 0.49, 0.36 against 2.79,
 * 1.92, 1.74 in rows 250-600), and its colour did not change with distance on the ground at all
 * where the painting's does over 15-240 m. The painting's parcels do not step at their ditches, so
 * the variety is soft patches, not whole parcels in turn.
 *
 * So a field takes, where the painting shows it, the colour the painter laid there
 * (src/data/fieldColourMap.json, tools/fields), and everywhere a tone of three independent lanes of
 * value noise on 60, 120 and 240 m lattices in the layout plane, along the three principal
 * directions of the painting's parcel-mean covariance in rows 100-600 (650 parcels). The lanes'
 * parcel means keep 0.61 of their spread in the painter view (the Kuwahara pass and the parcels'
 * own averaging), so the tone carries the painting's spread over that. Where the painted colour
 * stands whole it adds none: refitted on the 18 m map (terrain-colour-2, 2026-09-28), the map places
 * the painter's own parcel-scale variety, and lanes laid over it at random only moved the painter
 * view's grid cells off the painting's; the fine tone (fieldTexture) carries the variety within the
 * parcels. Each lattice fades out as a pixel comes to span a quarter of its cell, so the far fields
 * do not shimmer.
 */
import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  RGBAFormat,
  SRGBColorSpace,
  UnsignedByteType,
  Vector2,
  Vector3,
  Vector4,
  type IUniform,
} from 'three';
import { srgbChannelToLinear, type Lab, type LinearRGB } from '../core/color';
import { clamp, smoothstep } from '../core/math';
import type { PaletteModel } from '../data/types';
import type { FieldColourLayer, FieldColourMap } from '../data/fieldColourMap';
import { fieldLattice } from './fieldTexture';
import { toneLogMultipliers } from './fieldTextureColours';

export const PARCEL_TONE = {
  octaves: [
    { cellM: 60, weight: 1 },
    { cellM: 120, weight: 1 },
    { cellM: 240, weight: 0.7 },
  ],
  /* Spread of the octaves' weighted sum at full weight, which the lanes are divided by: a lattice's
     lane varies by 1/12 at its nodes and by (26/35)^2 of that between them, smoothstep weighing two
     independent nodes on each axis, so one lattice spreads 0.21445 and the three 0.3384. */
  noiseStd: 0.3384,
  fadeFrom: 0.125,
  fadeTo: 0.25,
  seed: 1_000_000,
  mapVarianceShare: 1,
  gain: 1.64,
} as const;

export const PARCEL_TONE_AXES = [
  { lab: [0.85, -0.47, 0.24] as Lab, std: 2.66 },
  { lab: [0.03, 0.5, 0.87] as Lab, std: 1.72 },
  { lab: [0.53, 0.73, -0.44] as Lab, std: 0.88 },
] as const;

function parcelLattice(u: number, v: number, seed: number): readonly number[] {
  const [i, j] = [Math.floor(u), Math.floor(v)];
  const [sx, sy] = [smoothstep(0, 1, u - i), smoothstep(0, 1, v - j)];
  const [a, b, c, d] = [
    fieldLattice(i, j, seed),
    fieldLattice(i + 1, j, seed),
    fieldLattice(i, j + 1, seed),
    fieldLattice(i + 1, j + 1, seed),
  ];
  return [0, 1, 2].map(
    (k) => (a[k]! * (1 - sx) + b[k]! * sx) * (1 - sy) + (c[k]! * (1 - sx) + d[k]! * sx) * sy
  );
}

function octaveLanes(x: number, y: number, octave: number, footprintM: number): readonly number[] {
  const { cellM, weight } = PARCEL_TONE.octaves[octave]!;
  const kept =
    weight * (1 - smoothstep(cellM * PARCEL_TONE.fadeFrom, cellM * PARCEL_TONE.fadeTo, footprintM));
  if (kept <= 0) return [0, 0, 0, 0];
  const lanes = parcelLattice(x / cellM, y / cellM, PARCEL_TONE.seed + octave);
  return [...lanes.map((lane) => kept * (lane - 0.5)), kept * kept];
}

const WEIGHT_SQUARES = PARCEL_TONE.octaves.reduce((sum, o) => sum + o.weight * o.weight, 0);

export interface ParcelTone {
  /** The three lanes, in standard deviations of a lane at full weight. */
  readonly lanes: readonly [number, number, number];
  /** Their variance there, as a share of full weight: the fades and the painted colour take some. */
  readonly variance: number;
}

export function parcelTone(
  x: number,
  y: number,
  footprintM: number,
  mapWeight: number
): ParcelTone {
  const sums = [0, 0, 0, 0];
  PARCEL_TONE.octaves.forEach((_, octave) => {
    octaveLanes(x, y, octave, footprintM).forEach((value, k) => {
      sums[k] = (sums[k] ?? 0) + value;
    });
  });
  const share = 1 - PARCEL_TONE.mapVarianceShare * mapWeight;
  const scale = Math.sqrt(share) / PARCEL_TONE.noiseStd;
  return {
    lanes: [sums[0]! * scale, sums[1]! * scale, sums[2]! * scale],
    variance: (sums[3]! / WEIGHT_SQUARES) * share,
  };
}

export function parcelToneMultipliers(
  palette: PaletteModel
): readonly [LinearRGB, LinearRGB, LinearRGB] {
  const field = palette.farFields.median.linear;
  const [a, b, c] = PARCEL_TONE_AXES.map((axis) =>
    toneLogMultipliers(field, axis.lab, axis.std * PARCEL_TONE.gain)
  );
  return [a!, b!, c!];
}

function writeLayer(data: Uint8Array, layer: FieldColourLayer, width: number, firstRow: number) {
  const { nx, ny } = layer.grid;
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const k = j * nx + i;
      const t = (firstRow + j) * width + i;
      data.set(layer.albedo.subarray(k * 3, k * 3 + 3), t * 4);
      data[t * 4 + 3] = layer.weight[k]!;
    }
  }
}

/** One texture for both layers: the fine one in the first rows, the far one in the rows above it. */
export function fieldColourTexture(map: FieldColourMap): DataTexture {
  const width = Math.max(map.grid.nx, map.far.grid.nx);
  const height = map.grid.ny + map.far.grid.ny;
  const data = new Uint8Array(width * height * 4);
  writeLayer(data, map, width, 0);
  writeLayer(data, map.far, width, map.grid.ny);
  const texture = new DataTexture(data, width, height, RGBAFormat, UnsignedByteType);
  texture.colorSpace = SRGBColorSpace;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

export interface FieldColourUniforms {
  uFieldColourMap: IUniform<DataTexture>;
  uFieldColourMapOrigin: IUniform<Vector2>;
  uFieldColourMapSize: IUniform<Vector2>;
  uFieldFarMapOrigin: IUniform<Vector2>;
  uFieldFarMapSize: IUniform<Vector2>;
  /** Cells of the fine layer (x, y) and of the far layer (z, w). */
  uFieldColourMapCells: IUniform<Vector4>;
  uFieldParcelA: IUniform<Vector3>;
  uFieldParcelB: IUniform<Vector3>;
  uFieldParcelC: IUniform<Vector3>;
}

export function fieldColourUniforms(
  palette: PaletteModel,
  map: FieldColourMap
): FieldColourUniforms {
  const { x0, y0, cellM, nx, ny } = map.grid;
  const far = map.far.grid;
  const [a, b, c] = parcelToneMultipliers(palette);
  return {
    uFieldColourMap: { value: fieldColourTexture(map) },
    uFieldColourMapOrigin: { value: new Vector2(x0, y0) },
    uFieldColourMapSize: { value: new Vector2(nx * cellM, ny * cellM) },
    uFieldFarMapOrigin: { value: new Vector2(far.x0, far.y0) },
    uFieldFarMapSize: { value: new Vector2(far.nx * far.cellM, far.ny * far.cellM) },
    uFieldColourMapCells: { value: new Vector4(nx, ny, far.nx, far.ny) },
    uFieldParcelA: { value: new Vector3(a[0], a[1], a[2]) },
    uFieldParcelB: { value: new Vector3(b[0], b[1], b[2]) },
    uFieldParcelC: { value: new Vector3(c[0], c[1], c[2]) },
  };
}

function cellAt(layer: FieldColourLayer, i: number, j: number): readonly number[] {
  const k = clamp(j, 0, layer.grid.ny - 1) * layer.grid.nx + clamp(i, 0, layer.grid.nx - 1);
  return [
    ...[0, 1, 2].map((ch) => srgbChannelToLinear(layer.albedo[k * 3 + ch]! / 255)),
    layer.weight[k]! / 255,
  ];
}

interface PaintedAt {
  readonly albedo: LinearRGB;
  readonly weight: number;
}

function layerAt(layer: FieldColourLayer, x: number, y: number): PaintedAt {
  const { x0, y0, cellM, nx, ny } = layer.grid;
  const u = (x - x0) / cellM;
  const v = (y - y0) / cellM;
  if (u < 0 || v < 0 || u > nx || v > ny) return { albedo: [0, 0, 0], weight: 0 };
  const [i, j] = [Math.floor(u - 0.5), Math.floor(v - 0.5)];
  const [tx, ty] = [u - 0.5 - i, v - 0.5 - j];
  const [c00, c10, c01, c11] = [
    cellAt(layer, i, j),
    cellAt(layer, i + 1, j),
    cellAt(layer, i, j + 1),
    cellAt(layer, i + 1, j + 1),
  ];
  const mixed = [0, 1, 2, 3].map(
    (k) => (c00[k]! * (1 - tx) + c10[k]! * tx) * (1 - ty) + (c01[k]! * (1 - tx) + c11[k]! * tx) * ty
  );
  return { albedo: [mixed[0]!, mixed[1]!, mixed[2]!], weight: mixed[3]! };
}

/** What the texture lookups read at a layout point: each layer bilinear between its cell centres and
 *  none off its grid, the fine layer laid over the far one by its weight. */
export function fieldColourMapAt(map: FieldColourMap, x: number, y: number): PaintedAt {
  const near = layerAt(map, x, y);
  const far = layerAt(map.far, x, y);
  const weight = near.weight + (1 - near.weight) * far.weight;
  if (weight <= 0) return { albedo: [0, 0, 0], weight: 0 };
  const channel = (ch: 0 | 1 | 2) =>
    (near.albedo[ch] * near.weight + far.albedo[ch] * far.weight * (1 - near.weight)) / weight;
  return { albedo: [channel(0), channel(1), channel(2)], weight };
}

/**
 * A sixth of the log multipliers' variance comes off, so each channel's cube root, which Lab
 * reads, keeps its mean under the tone.
 */
export function paintedFieldColour(
  vertexColour: LinearRGB,
  map: FieldColourMap,
  at: readonly [number, number],
  footprintM: number,
  multipliers: readonly [LinearRGB, LinearRGB, LinearRGB]
): LinearRGB {
  const painted = fieldColourMapAt(map, at[0], at[1]);
  const { lanes, variance } = parcelTone(at[0], at[1], footprintM, painted.weight);
  const channel = (ch: 0 | 1 | 2): number => {
    const base = vertexColour[ch] * (1 - painted.weight) + painted.albedo[ch] * painted.weight;
    const spread = multipliers.reduce((sum, m) => sum + m[ch] * m[ch], 0);
    const log = multipliers.reduce((sum, m, lane) => sum + lanes[lane]! * m[ch], 0);
    return base * Math.exp(log - (variance * spread) / 6);
  };
  return [channel(0), channel(1), channel(2)];
}
